import { AdConfig } from '../types';
import { StorageService } from './storageService';

const STORAGE_KEY = 'cna_sponsored_config_v3';
const API_URL = '/practice-exams/api/ad-config';

const DEFAULT_CONFIG: AdConfig = {
  appName: 'CNA Prep Pro',
  isActive: true,
  isMaintenanceMode: false,
  maintenanceTitle: 'System Calibration',
  maintenanceMessage: 'We are currently performing infrastructure updates. Registry access will resume shortly.',
  isPwaEnabled: true,
  isWalkthroughEnabled: true, 
  intermissionEnabled: true,
  isPodcastsEnabled: true,
  isMiniPlayerEnabled: true,
  miniPlayerTracks: undefined,
  isRegistrySearchEnabled: true,
  isStudyPlannerEnabled: true,
  isDiagnosticEnabled: true,
  isStreaksEnabled: true,
  isWeakPointRemediationEnabled: true,
  isSocialProofEnabled: true,
  isReadinessForecastEnabled: true,
  isMathLabAiEnabled: true,
  isFixedTestsEnabled: true, 
  isAnalyticsEnabled: true,
  isQuickQuizEnabled: true,
  isVoiceSequencingEnabled: true,
  onboardingFeatureEnabled: true,
  onboardingHoursThreshold: 48,
  isSkillsPracticeModeEnabled: true,
  isClinicalSimulatorEnabled: true,
  clinicalSimulatorEnabledCerts: ['cna', 'phlebotomy', 'medical_assistant', 'pharmacy_tech', 'pct'],
  isGuestTrialEnabled: true,
  guestQuestionLimit: 50,
  guestExamLimit: 1,
  isFooterEnabled: true,
  footerType: 'standard',
  headerMode: 'fixed',
  headerSlimMobile: true,
  schoolLogoUrl: 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg',
  showUpgradeHint: true, 
  enableScenarioAIImages: true,
  enableAIRationales: true,
  enableVibrantCards: true,
  activeTheme: 'default',
  eliteProUrl: 'https://www.diamondnursingschool.com/plans-pricing',
  eliteProPrice: '$49',
  eliteProVideoUrl: '',
  googleAdsScript: '',
  metaPixelScript: '',
  customFooterScript: '',
  gaTrackingId: '',
  gaEnabled: false,
  gaEnableInDev: false,
  metaPixelId: '',
  metaPixelEnabled: false,
  metaPixelEnableInDev: false,
  llmsTxtTemplate: '',
  type: 'image',
  title: 'Antimicrobial Scrubs',
  description: 'Premium medical gear for CNA professionals. Use code CNAPREP.',
  mediaUrl: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=600',
  ctaText: 'Claim 20% Discount',
  targetUrl: 'https://example.com/scrubs',
  cookieConsent: {
    isEnabled: false,
    title: 'Cookie Consent',
    message: 'We use cookies to enhance your experience and analyze our traffic.',
    acceptButtonText: 'Accept All',
    declineButtonText: 'Decline',
    policyUrl: '/privacy'
  },
  overlaySettings: { enabled: true, triggerMinutes: 10, skipSeconds: 10 },
  intervals: {
    studyGuide: 5,
    skills_lab: 4,
    practiceQuiz: 7,
    practiceExam: 10,
    scenarioQuiz: 5,
    flashcards: 10,
    intermissionSkipSeconds: 5,
    adRotationThreshold: 10 // Default: every 10 actions
  },
  placements: {
    dashboard: { enabled: true, source: 'internal', htmlSnippet: '' },
    dashboard_welcome_right: { enabled: true, source: 'internal', htmlSnippet: '' },
    sidebar: { enabled: true, source: 'internal', htmlSnippet: '' },
    profile: { enabled: true, source: 'internal', htmlSnippet: '' },
    results: { enabled: true, source: 'internal', htmlSnippet: '' },
    inline_quiz: { enabled: true, source: 'internal', htmlSnippet: '' },
    footer_mobile: { enabled: true, source: 'internal', htmlSnippet: '' },
    overlay: { enabled: true, source: 'internal', htmlSnippet: '' },
    study_guide_top: { enabled: true, source: 'internal', htmlSnippet: '' },
    study_guide_bottom: { enabled: true, source: 'internal', htmlSnippet: '' },
    skills_lab_top: { enabled: true, source: 'internal', htmlSnippet: '' },
    skills_lab_bottom: { enabled: true, source: 'internal', htmlSnippet: '' },
    practice_exam_top: { enabled: true, source: 'internal', htmlSnippet: '' },
    practice_exam_bottom: { enabled: true, source: 'internal', htmlSnippet: '' },
    practice_quiz_top: { enabled: true, source: 'internal', htmlSnippet: '' },
    practice_quiz_bottom: { enabled: true, source: 'internal', htmlSnippet: '' },
    history_list: { enabled: true, source: 'internal', htmlSnippet: '' },
    history_details: { enabled: true, source: 'internal', htmlSnippet: '' },
    ai_tutor: { enabled: true, source: 'internal', htmlSnippet: '' },
    missed_bank: { enabled: true, source: 'internal', htmlSnippet: '' },
    math_lab_dashboard: { enabled: true, source: 'internal', htmlSnippet: '' },
    math_lab_sidebar: { enabled: true, source: 'internal', htmlSnippet: '' },
    podcasts_top: { enabled: true, source: 'internal', htmlSnippet: '' },
    podcasts_sidebar: { enabled: true, source: 'internal', htmlSnippet: '' },
    podcasts_player_sticky: { enabled: true, source: 'internal', htmlSnippet: '' },
    dashboard_bottom: { enabled: true, source: 'internal', htmlSnippet: '' },
    flashcards_top: { enabled: true, source: 'internal', htmlSnippet: '' }
  }
};

let cachedConfig: AdConfig | null = null;

export const SponsoredService = {
  getConfig: (): AdConfig => {
    if (cachedConfig) return cachedConfig;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        cachedConfig = { 
          ...DEFAULT_CONFIG, 
          ...parsed,
          cookieConsent: { ...DEFAULT_CONFIG.cookieConsent, ...(parsed.cookieConsent || {}) },
          intervals: { ...DEFAULT_CONFIG.intervals, ...(parsed.intervals || {}) },
          placements: { ...DEFAULT_CONFIG.placements, ...(parsed.placements || {}) }
        };
        return cachedConfig!;
      }
    } catch (e) {}
    return DEFAULT_CONFIG;
  },

  fetchLatestConfig: async (): Promise<AdConfig> => {
    try {
      const res = await fetch(API_URL);
      if (res.ok) {
        const config = await res.json();
        cachedConfig = { 
          ...DEFAULT_CONFIG, 
          ...config,
          cookieConsent: { ...DEFAULT_CONFIG.cookieConsent, ...(config.cookieConsent || {}) },
          intervals: { ...DEFAULT_CONFIG.intervals, ...(config.intervals || {}) },
          placements: { ...DEFAULT_CONFIG.placements, ...(config.placements || {}) }
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedConfig));
        window.dispatchEvent(new CustomEvent('sponsored-config-updated'));
        return cachedConfig!;
      }
    } catch (e) {}
    return SponsoredService.getConfig();
  },

  updateConfig: async (newConfig: AdConfig): Promise<AdConfig> => {
    try {
      const res = await fetch(API_URL, {
        method: 'PUT',
        headers: StorageService.getAuthHeader(),
        body: JSON.stringify(newConfig)
      });
      if (res.ok) {
        const savedData = await res.json();
        // Robust merge: Favor newConfig (the truth we just pushed) over savedData
        cachedConfig = { 
          ...savedData,
          ...newConfig,
          cookieConsent: { ...(savedData.cookieConsent || {}), ...newConfig.cookieConsent },
          intervals: { ...(savedData.intervals || {}), ...newConfig.intervals },
          placements: { ...(savedData.placements || {}), ...newConfig.placements }
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedConfig));
        window.dispatchEvent(new CustomEvent('sponsored-config-updated'));
        return cachedConfig!;
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error("[SponsoredService] Registry rejection:", errorData.error || res.statusText);
      }
    } catch (e) {
      console.error("[SponsoredService] Failed to sync ad config to registry.", e);
    }
    
    cachedConfig = newConfig;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
    window.dispatchEvent(new CustomEvent('sponsored-config-updated'));
    return newConfig;
  }
};